import { useEffect, useState } from 'react';
import type { CostRow } from '@trivio/contracts';
import { type CostGroupBy, getCosts } from '../../lib/api-client';

export interface UseCostsResult {
  rows: CostRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * `GET /v1/costs` (TTP-250 / TRV-19) — no cursor pagination, unlike
 * `useLabels.ts` (the endpoint always returns the full month/tenant array,
 * see `CostRow`'s docstring), so this hook is the simpler subset of that
 * pattern: re-fetch from scratch whenever `apiKey`, `period` or `groupBy`
 * change. `apiKey` itself changes whenever the dashboard session key is
 * re-ensured under a different org (`DashboardSessionKeyContext.tsx`), so
 * switching orgs via `OrgSwitcher` reloads this list for free, same as
 * every other `/v1` list hook in this app.
 */
export function useCosts(apiKey: string | null, period: string, groupBy: CostGroupBy | ''): UseCostsResult {
  const [rows, setRows] = useState<CostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!apiKey || !period) {
      setRows([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getCosts(apiKey, { period, ...(groupBy ? { group_by: groupBy } : {}) })
      .then((result) => {
        if (cancelled) return;
        setRows(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Não foi possível carregar os custos.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, period, groupBy, reloadToken]);

  const refetch = () => setReloadToken((t) => t + 1);

  return { rows, loading, error, refetch };
}
